/* eslint-disable import/no-named-as-default */
import React, { useEffect, useState } from 'react';
import Router, { useRouter } from 'next/router';
import toLower from 'ramda/src/toLower';
import isEmpty from 'ramda/src/isEmpty';
import identity from 'ramda/src/identity';
import filtersStyles from '../Filters/filters.module.scss';
import GalleryItem from '../Gallery/GalleryItem/GalleryItem';
import GalleryHeader from '../Gallery/GalleryHeader/GalleryHeader';
import useTemplates from '../../hooks/gallery/useTemplates';
import useDevice from '../../hooks/useDevice';
import { useStateValue } from '../../state/stateProvider';
import { FiltersElement, Template } from '../types';
import Filters from '../Filters/Filters';
import Loader from '../Loader/Loader';

import classes from './skeleton.module.scss';

const Skeleton = ({
  user,
  hasHeaderNotification,
  content,
}): JSX.Element => {
  const {
    skeletonWrapper, container, layout, redCircle, purpleXVector, noResultsMessage, iconsContainer,
  } = classes;

  const SECTION_NAME = 'gallery';

  const { isOnIframe, hasTrialBar } = filtersStyles;
  const router = useRouter();
  const [queryParamFilter, setQueryParamFilter] = useState(null);
  const { data, loading, error } = useTemplates(content?.vertical);
  const [{ filters }] = useStateValue();
  const [{ isIframe }] = useStateValue();
  const { isMobile } = useDevice();

  const userIsLoggedIn = () => user && user.userId;

  /**
   * Assembling relevant local storage pointer which holds
   * recent filters preset
   */
  const getLocalStoragePointer = () => {
    if (!userIsLoggedIn()) {
      return '';
    }
    return `${user.userId}_FiltersPreset`;
  };

  const patchData = [
    {
      tagValues: ['Tickets'],
      valuesToEnrich: ['Tickets Sales'],
    },
    {
      tagValues: ['personalities'],
      valuesToEnrich: ['Segmentation', 'Journey Personalization', 'personality'],
    },
    {
      tagValues: ['stories'],
      valuesToEnrich: ['Advocacy', 'story'],
    },
    {
      tagValues: ['quizzes', 'polls', 'Subscription'],
      valuesToEnrich: ['Collect Data'],
    },
    {
      tagValues: ['polls'],
      valuesToEnrich: ['poll'],
    },
    {
      tagValues: ['video'],
      valuesToEnrich: ['videos'],
    },
    {
      tagValues: ['galleries'],
      valuesToEnrich: ['gallery'],
    },
    {
      tagValues: ['quizzes'],
      valuesToEnrich: ['quiz'],
    },
    {
      tagValues: [],
      valuesToEnrich: ['monetization'],
    },
  ];

  const filterTemplates = ({ needs, filter, timeline }: FiltersElement) => ({ tags }: Template) => {
    patchData.forEach((patchItem) => {
      const shouldAddPatch = !patchItem.tagValues.length || tags.some((tag) => patchItem.tagValues.includes(tag));
      if (shouldAddPatch && !tags.includes(patchItem.valuesToEnrich[0])) {
        tags.push(...patchItem.valuesToEnrich);
      }
    });

    // Filter needs to only include values valid for the current vertical
    const validNeeds = content?.filters?.needs || [];
    const validNeedsLower = validNeeds.map(toLower);
    const filteredNeeds = needs.filter((need) => validNeedsLower.includes(toLower(need)));

    const filterByNeeds = isEmpty(filteredNeeds) ? true : filteredNeeds
      .map(toLower)
      .some((need) => tags.map(toLower).includes(need));

    const filterByMoreFilters = [filter, timeline]
      .filter(identity)
      .map(toLower)
      .every((option) => tags.map(toLower).includes(option));
    return filterByNeeds && filterByMoreFilters;
  };

  useEffect(() => {
    const { query } = router;
    if (query.filter) {
      setQueryParamFilter(query.filter);
      delete query.filter;
      Router.replace({ query });
    }
  }, [router.query.filter]);

  if (loading || !data) {
    return <Loader/>;
  }

  if (error) {
    return <h1>{error}</h1>;
  }

  /**
     * Filters bar sticky position should be adjusted for different use cases:
     * When there is no header at all or when header has trial notification
     * sticky filters should stick at different position.
     */
  const adjustFiltersStickyPos = () => {
    if (isMobile || user === 'error' || !user || isIframe) {
      return isOnIframe;
    }
    return hasHeaderNotification ? hasTrialBar : '';
  };

  /**
     * Generating list of gallery items
     */
  const generateGalleryItems = () => {
    const filteredResults = data?.filter(filterTemplates(filters));
    return filteredResults?.length ? filteredResults?.map((template, index) => (
              <GalleryItem
                  templateIndex={index}
                  template={template}
                  key={Math.random()}
                  sectionName={SECTION_NAME}
                  needs={filters.needs}
              />)) : <div className={noResultsMessage}>It looks like there aren't templates matched</div>;
  };

  return (
    <div className={skeletonWrapper}>
      <GalleryHeader content={content}/>
      <div className={layout}>
        <div className={iconsContainer}>
          <img src='/o-vector.png' className={redCircle} alt='circle' />
          <img src='/purple-x-vector.png' className={purpleXVector} alt='circle' />
        </div>
        <div className={container}>
          <Filters content={content}
                   queryParamFilter={queryParamFilter}
                   isMobile={isMobile}
                   adjustFiltersClass={adjustFiltersStickyPos()}
                   localStoragePointer={getLocalStoragePointer()} />
          <div className="responsiveGridLayout">
            <div className="responsiveMainContent templateGridRow">
              { generateGalleryItems() }
            </div>
          </div>
        </div>
      </div>
   </div>
  );
};

export default Skeleton;
